import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  FileText, Lock, Unlock, Shield, Clock, AlertTriangle,
  Download, Eye, CheckCircle2, XCircle, ExternalLink,
  File, FileImage, FileBadge, BookOpen, Award
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { JobDocumentUpload, JobDocumentCategory } from '@/types/jobWizard';
import { motion } from 'framer-motion';

interface JobDocumentGalleryProps {
  documents: JobDocumentUpload[];
  canAccess?: boolean;
  onRequestAccess?: () => void;
}

const DOCUMENT_CATEGORY_CONFIG: Record<JobDocumentCategory, { 
  label: string; 
  icon: React.ElementType; 
  color: string;
}> = {
  resume: { label: 'Resume/CV', icon: FileText, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  cover_letter: { label: 'Cover Letter', icon: FileText, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  pilot_license: { label: 'Pilot License', icon: FileBadge, color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  medical_certificate: { label: 'Medical Certificate', icon: Shield, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  mechanic_license: { label: 'Mechanic License', icon: FileBadge, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  type_rating_cert: { label: 'Type Rating', icon: Award, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  training_records: { label: 'Training Records', icon: BookOpen, color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  logbook_summary: { label: 'Logbook Summary', icon: BookOpen, color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  education: { label: 'Education', icon: BookOpen, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  references: { label: 'References', icon: FileText, color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  portfolio: { label: 'Portfolio', icon: FileImage, color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
  background_check: { label: 'Background Check', icon: Shield, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  other: { label: 'Other', icon: File, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

const VERIFICATION_STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  verified: { label: 'Verified', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  expired: { label: 'Expired', icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const JobDocumentGallery: React.FC<JobDocumentGalleryProps> = ({
  documents,
  canAccess = false,
  onRequestAccess,
}) => {
  if (!documents || documents.length === 0) return null;

  const publicDocs = documents.filter(d => !d.isConfidential);
  const confidentialDocs = documents.filter(d => d.isConfidential);

  const renderDocumentCard = (doc: JobDocumentUpload, index: number, isLocked: boolean) => {
    const config = DOCUMENT_CATEGORY_CONFIG[doc.category] || DOCUMENT_CATEGORY_CONFIG.other;
    const Icon = config.icon;
    const verificationConfig = doc.verificationStatus ? VERIFICATION_STATUS_CONFIG[doc.verificationStatus] : null;
    const VerificationIcon = verificationConfig?.icon;

    return (
      <motion.div
        key={doc.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={cn(
          "relative p-4 rounded-xl border transition-all group",
          isLocked 
            ? "bg-muted/30 border-dashed cursor-not-allowed" 
            : "bg-card hover:shadow-md hover:border-primary/30"
        )}
      >
        {/* Locked Overlay */}
        {isLocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[2px] rounded-xl z-10">
            <div className="text-center">
              <Lock className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">Confidential</p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          {/* Document Icon */}
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            config.color
          )}>
            <Icon className="w-5 h-5" />
          </div>

          {/* Document Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm truncate">{doc.name}</h4>
              {doc.isConfidential && !isLocked && (
                <Unlock className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {config.label}
              </Badge>
              
              {verificationConfig && VerificationIcon && (
                <Badge className={cn("text-[10px] px-1.5 py-0 gap-0.5", verificationConfig.color)}>
                  <VerificationIcon className="w-2.5 h-2.5" />
                  {verificationConfig.label}
                </Badge>
              )}
            </div>

            {doc.expiresAt && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1.5">
                <AlertTriangle className="w-3 h-3" />
                <span>Expires: {new Date(doc.expiresAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          {!isLocked && doc.url && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  <Eye className="w-4 h-4" />
                </a>
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href={doc.url} download>
                  <Download className="w-4 h-4" />
                </a>
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" />
          Documents
          <Badge variant="secondary" className="ml-auto text-xs">
            {documents.length} {documents.length === 1 ? 'File' : 'Files'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Public Documents */}
        {publicDocs.length > 0 && (
          <div className="space-y-2">
            {publicDocs.map((doc, i) => renderDocumentCard(doc, i, false))}
          </div>
        )}

        {/* Confidential Documents Section */}
        {confidentialDocs.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="w-4 h-4" />
              <span>Confidential Documents ({confidentialDocs.length})</span>
            </div>
            
            {canAccess ? (
              <div className="space-y-2">
                {confidentialDocs.map((doc, i) => renderDocumentCard(doc, i, false))}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {confidentialDocs.map((doc, i) => renderDocumentCard(doc, i, true))}
                </div>
                
                {onRequestAccess && (
                  <Button 
                    variant="outline" 
                    className="w-full mt-3 gap-2"
                    onClick={onRequestAccess}
                  >
                    <Shield className="w-4 h-4" />
                    Request Access to Documents
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDocumentGallery;
